import { useState, useRef } from 'react';

const testimonials = [
  {
    name: 'Sarah Mitchell',
    location: 'Springfield',
    rating: 5,
    review: 'Outstanding service from start to finish! They completely transformed our kitchen and the quality of work exceeded our expectations. The team was professional, punctual, and respectful of our home.',
    avatar: 'SM'
  },
  {
    name: 'Michael Rodriguez',
    location: 'Riverside',
    rating: 5,
    review: 'I called them for an emergency plumbing issue and they arrived within an hour. The technician was knowledgeable, fixed the problem quickly, and the pricing was very fair. Highly recommend!',
    avatar: 'MR'
  },
  {
    name: 'Jennifer Thompson',
    location: 'Lakewood',
    rating: 5,
    review: 'We hired them for a complete bathroom renovation and couldn\'t be happier with the results. They handled everything professionally and kept us informed throughout the entire process. Worth every penny!',
    avatar: 'JT'
  }
];

function TestimonialCard({ testimonial }: { testimonial: typeof testimonials[0] }) {
  return (
    <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-lg hover:shadow-xl transition-shadow duration-300 w-full flex-shrink-0">
      <div className="flex gap-1 mb-6">
        {[...Array(testimonial.rating)].map((_, i) => (
          <i key={i} className="ri-star-fill text-xl text-[#FDB022]"></i>
        ))}
      </div>
      <p className="text-base sm:text-lg text-[#4A4A4A] leading-relaxed mb-6">
        "{testimonial.review}"
      </p>
      <div className="flex items-center">
        <div className="w-12 h-12 bg-[#1e40af] rounded-full flex items-center justify-center text-white font-bold text-base flex-shrink-0">
          {testimonial.avatar}
        </div>
        <div className="ml-4">
          <h4 className="text-base font-bold text-[#2C2C2C]">{testimonial.name}</h4>
          <p className="text-sm text-[#6B6B6B]">{testimonial.location}</p>
        </div>
      </div>
    </div>
  );
}

export default function Testimonials() {
  const [current, setCurrent] = useState(0);
  const touchStartX = useRef<number | null>(null);

  const prev = () => setCurrent(c => (c === 0 ? testimonials.length - 1 : c - 1));
  const next = () => setCurrent(c => (c === testimonials.length - 1 ? 0 : c + 1));

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) diff > 0 ? next() : prev();
    touchStartX.current = null;
  };

  return (
    <section id="testimonials" className="py-20 md:py-36 px-4 sm:px-6 bg-[#F8F9FA]">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-20">
          <p className="text-[#1e40af] text-xs font-bold uppercase tracking-widest mb-4">TESTIMONIALS</p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#2C2C2C]">What Our Customers Say</h2>
        </div>

        {/* Desktop Grid */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard key={index} testimonial={testimonial} />
          ))}
        </div>

        {/* Mobile Slider */}
        <div className="md:hidden">
          <div
            className="overflow-hidden rounded-2xl"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <div
              className="flex transition-transform duration-300 ease-in-out"
              style={{ transform: `translateX(-${current * 100}%)` }}
            >
              {testimonials.map((testimonial, index) => (
                <div key={index} className="w-full flex-shrink-0">
                  <TestimonialCard testimonial={testimonial} />
                </div>
              ))}
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-between mt-5 px-1">
            <button
              onClick={prev}
              className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 bg-white shadow-sm hover:bg-gray-50 cursor-pointer"
              aria-label="Previous"
            >
              <i className="ri-arrow-left-s-line text-xl text-gray-600"></i>
            </button>

            {/* Dots */}
            <div className="flex gap-2">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  className={`w-2 h-2 rounded-full transition-all cursor-pointer ${i === current ? 'bg-[#1e40af] w-5' : 'bg-gray-300'}`}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>

            <button
              onClick={next}
              className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 bg-white shadow-sm hover:bg-gray-50 cursor-pointer"
              aria-label="Next"
            >
              <i className="ri-arrow-right-s-line text-xl text-gray-600"></i>
            </button>
          </div>

          <p className="text-center text-sm text-gray-400 mt-3">{current + 1} / {testimonials.length}</p>
        </div>
      </div>
    </section>
  );
}
